import React from 'react';
import { Pressable, Text, View } from 'react-native';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import CutCard from '@/components/General/CutCard';
import DropdownMenu from '@/components/General/DropdownMenu';
import { SERVICE_FLOW_ITEM_TYPES } from '@/config/serviceFlows';
import { formatDurationLabel } from '@/utils/serviceFlow';
import ServiceFlowNotesBody from './ServiceFlowNotesBody';
import { serviceFlowStyles } from './serviceFlowStyles';

/**
 * One rail row in a Service Flow timeline (spec §3.4/§3.5).
 *
 * Purely presentational — the parent supplies the time label + children
 * (e.g. an embedded TaskCard for TASK occurrences) and row-menu callbacks.
 * The cue marker renders inside the header row so it vertically centers on
 * the title on every platform.
 */
const ServiceFlowTimelineRow = ({
    item,
    timeLabel,
    dayOffset = 0,
    compact: _compact = false,
    selected = false,
    canEdit: _canEdit = false,
    isFirst = false,
    isLast = false,
    onPress = null,
    onEdit = null,
    onDuplicate = null,
    onRemove = null,
    onMove = null,
    children = null,
}) => {
    const typeConfig = SERVICE_FLOW_ITEM_TYPES[item?.type] || SERVICE_FLOW_ITEM_TYPES.AGENDA;
    const chipVariant = item?.type === 'TASK' ? 'tasks' : item?.type === 'WORSHIP' ? 'events' : 'textSecondary';
    const hasNotes = !!(item?.description || '').trim();
    // No placeholder meta — rows only describe what is actually set. The
    // timeline gates timeLabel on item.showTime; without a duration the row
    // shows nothing at all.
    const durationLabel = Number.isInteger(item?.durationMinutes)
        ? formatDurationLabel(item.durationMinutes)
        : null;

    const menuOptions = [
        onEdit && { name: 'Edit item', onSelect: () => onEdit(item) },
        onMove && { name: 'Move earlier', disabled: isFirst, onSelect: () => onMove(item, -1) },
        onMove && { name: 'Move later', disabled: isLast, onSelect: () => onMove(item, 1) },
        onDuplicate && { name: 'Duplicate', onSelect: () => onDuplicate(item) },

        onRemove && { name: 'Remove from flow', isDestructive: true, onSelect: () => onRemove(item) },
    ].filter(Boolean);

    const menu = menuOptions.length > 0 ? (
        <DropdownMenu
            options={menuOptions}
            testID={`service-flow-item-menu-${item.id}`}
            triggerComponent={
                <Pressable
                    testID={`service-flow-item-details-${item.id}`}
                    accessibilityLabel={`${item.title || 'Item'} options`}
                    style={serviceFlowStyles.rowMenuButton}
                >
                    <Unicon name="ellipsis-vertical" size={16} colorVariant="textSecondary" />
                </Pressable>
            }
        />
    ) : null;

    return (
        <View style={serviceFlowStyles.row} testID={`service-flow-item-${item.id}`}>
            <View style={serviceFlowStyles.rowContent}>
                <View style={serviceFlowStyles.rowHeader}>
                    <View style={serviceFlowStyles.cueMarker} />
                    {onPress ? (
                        <Pressable
                            style={{ flex: 1 }}
                            onPress={() => onPress(item)}
                            testID={`service-flow-item-edit-${item.id}`}
                            accessibilityRole="button"
                            accessibilityLabel={`Edit ${item.title || 'item'}`}
                            accessibilityState={{ selected }}
                        >
                            <Text style={serviceFlowStyles.rowTitle(!item.title)} numberOfLines={2}>{item.title || 'Untitled'}</Text>
                        </Pressable>
                    ) : (
                        <Text style={serviceFlowStyles.rowTitle(!item.title)} numberOfLines={2}>{item.title || 'Untitled'}</Text>
                    )}
                    <Chip colorVariant={chipVariant} testID={`service-flow-item-type-${item.id}`}>{typeConfig.label}</Chip>
                    {menu}
                </View>

                {(durationLabel || children || hasNotes) && (
                    <CutCard
                        style={serviceFlowStyles.detailsCard}
                        contentStyle={serviceFlowStyles.detailsCardContent}
                        testID={`service-flow-item-body-${item.id}`}
                    >
                        {!!durationLabel && (
                            <Text style={serviceFlowStyles.rowMeta}>
                                {!!timeLabel && (
                                    <Text style={serviceFlowStyles.timeInline}>
                                        {dayOffset !== 0 ? `${dayOffset > 0 ? '+' : ''}${dayOffset}d ` : ''}{timeLabel}
                                    </Text>
                                )}
                                {timeLabel ? ' · ' : ''}
                                {durationLabel}
                            </Text>
                        )}

                        {children}
                        {hasNotes && (
                            <View>
                                <Text style={serviceFlowStyles.notesHeading}>Additional Notes</Text>
                                <ServiceFlowNotesBody text={item.description} />
                            </View>
                        )}
                    </CutCard>
                )}
            </View>
        </View>
    );

};

export default ServiceFlowTimelineRow;
